document.addEventListener('DOMContentLoaded', () => {
    const getMarketVariantName = () => {
        const script = document.querySelector('[data-free-product-mappings]');
        const marketId = document.querySelector('.bundle-picker')?.dataset.marketId || '';
        if (!script) return null;

        try {
            const mappings = JSON.parse(script.textContent);
            return mappings && typeof mappings === 'object' && !Array.isArray(mappings)
                ? mappings[marketId] || null
                : null;
        } catch (error) {
            console.error('BundlePicker: Invalid market mapping JSON', error);
            return null;
        }
    };
    const marketVariantName = getMarketVariantName();

    class BundlePicker extends HTMLElement {
        constructor() {
            super();

            this.configScript = null;
            this.bundleConfig = [];
            this.colorConfig = [];
            this.optionMap = null;

            this.boundHandleChange = this.handleChange.bind(this);

        }

        connectedCallback() {
            this.configScript = this.querySelector('script[data-variant]');
            this.configColorScript = this.querySelector('script[data-color]');
            if (!this.configScript) return;

            this.bundleConfig = this.parseConfig(this.configScript);
            this.colorConfig = this.parseConfig(this.configColorScript);
            this.colorMap = new Map(
                this.colorConfig.map((item) => [
                    item.name,
                    {
                        color: item.color,
                        label: item.label,
                        swatch: item.swatch ? item.swatch[String(item.name).toLocaleLowerCase()] : null,
                    },
                ])
            );
            this.optionMap = this.buildOptionMap(this.bundleConfig);

            if (!this.optionMap) return;

            this.render();
            this.bindEvents();
            this.dispatchInitEvent();
        }

        disconnectedCallback() {
            this.unbindEvents();
        }

        render() {
            const container = this.querySelector('.bundle-picker__inner');
            if (!container) {
                console.warn('BundlePicker: .bundle-picker__inner not found');
                return;
            }

            const fragment = document.createDocumentFragment();

            Object.entries(this.optionMap).forEach(([optionName, optionData]) => {
                const normalizedOptionName = this.normalizeOptionName(optionName);
                const relatedSelect = document.getElementById(normalizedOptionName);
                const selectedValue = relatedSelect?.value;
                const mappedValue = marketVariantName
                    ? optionData.values.find((value) => this.normalizeValue(value) === this.normalizeValue(marketVariantName))
                    : null;
                const preferredValue = mappedValue || selectedValue;

                const fieldset = document.createElement('fieldset');
                fieldset.dataset.optionName = optionName;
                fieldset.setAttribute("hidden", "");

                const legend = document.createElement('legend');
                const small = document.createElement('small');
                small.textContent = '';
                legend.textContent = `${optionName.replace('_', '')}: `;
                legend.appendChild(small);

                fieldset.appendChild(legend);
                const colorOptionFlags = [];
                optionData.values.forEach((value, index) => {
                    const id = `${normalizedOptionName}-${index}`;

                    const input = document.createElement('input');
                    input.type = 'radio';
                    input.name = normalizedOptionName;
                    input.value = value;
                    input.id = id;

                    if (preferredValue === value) {
                        input.checked = true;
                    }

                    const labelColor = optionData.colors[index] || value;
                    const colorInMap = this.colorMap.get(labelColor);

                    input.setAttribute('data-label', colorInMap?.label || labelColor);
                    const isColorOption = Boolean(colorInMap?.color || colorInMap?.swatch);
                    colorOptionFlags.push(isColorOption);
                    const label = document.createElement('label');
                    label.setAttribute('for', id);
                    const span = document.createElement('span');
                    if (label && colorInMap && colorInMap.color) {
                        span.style.setProperty('--swatch', colorInMap.color);
                        label.classList.add('is-swatch');
                        if (colorInMap.swatch) {
                            span.style.setProperty('--swatch-image', `url(${colorInMap.swatch})`);
                            label.classList.add('is-swatch--image');
                        }
                    }
                    span.textContent = optionData.colors[index] || value;
                    label.append(span);
                    fieldset.append(input, label);
                });

                const isNotColorOption = !colorOptionFlags.some(Boolean);
                fieldset.toggleAttribute("hidden", isNotColorOption);

                const allInputs = [...fieldset.querySelectorAll('input')];
                const matchingInput = allInputs.find((input) => input.value === preferredValue);
                allInputs.forEach((input) => {
                    input.checked = input === matchingInput;
                });
                if (!matchingInput && allInputs[0]) allInputs[0].checked = true;

                const checkedInput = fieldset.querySelector('input:checked');
                if (relatedSelect && checkedInput && relatedSelect.value !== checkedInput.value) {
                    relatedSelect.value = checkedInput.value;
                    relatedSelect.dispatchEvent(new Event('change', { bubbles: true }));
                }

                fragment.appendChild(fieldset);
            });

            container.appendChild(fragment);
        }

        bindEvents() {
            this.addEventListener('change', this.boundHandleChange);
        }

        unbindEvents() {
            this.removeEventListener('change', this.boundHandleChange);
        }

        handleChange(event) {
            const input = event.target;
            if (input.type !== 'radio') return;

            const fieldset = input.closest('fieldset');
            if (!fieldset) return;

            const optionName = fieldset.dataset.optionName;
            const normalizedOptionName = this.normalizeOptionName(optionName);

            this.syncSelect(normalizedOptionName, input.value);

            this.dispatchChangeEvent({
                optionName,
                value: input.value,
                inputElement: input,
            });
        }

        syncSelect(optionKey, value) {
            const select = this.offsetParent.querySelector('#' + CSS.escape(optionKey));
            if (!select) return;

            if (select.value !== value) {
                select.value = value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        dispatchInitEvent() {
            queueMicrotask(() => {
                this.dispatchEvent(
                    new CustomEvent('bundle-picker:init', {
                        bubbles: true,
                        detail: {
                            optionMap: this.optionMap,
                        },
                    })
                );
            });
        }

        dispatchChangeEvent(detail) {
            this.dispatchEvent(
                new CustomEvent('bundle-picker:change', {
                    bubbles: true,
                    detail: {
                        ...detail,
                        optionMap: this.optionMap,
                    },
                })
            );
        }

        parseConfig(scriptTag) {
            if (!scriptTag) return [];

            try {
                return JSON.parse(scriptTag.textContent) || [];
            } catch (error) {
                console.error('BundlePicker: Invalid JSON config', error);
                return [];
            }
        }

        normalizeValue(value = '') {
            return String(value).trim().toLocaleLowerCase();
        }

        buildOptionMap(config = []) {
            if (!Array.isArray(config) || !config.length) return null;

            const optionMap = {};

            config.forEach(({ optionName, optionValues }) => {
                if (!optionName || !optionValues) return;

                const values = String(optionValues)
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);

                if (values.length < 2) return;

                optionMap[optionName] = { values };
                optionMap[optionName].colors = [];
                values.forEach((value) => {
                    const normalizedValue = value
                        .split('-')
                        .map((element) => element.trim().toLocaleLowerCase())
                        .splice(1)
                        .join('-');
                    optionMap[optionName].colors.push(normalizedValue);
                });
            });

            return optionMap;
        }

        changeLagendText() {
            this.querySelectorAll('fieldset').forEach((fieldset) => {
                const labelValue = fieldset.querySelector('legend small');
                const checkedInput = fieldset.querySelector('input:checked');

                if (!labelValue || !checkedInput) return;

                const {
                    dataset: { label },
                } = checkedInput;
                if (label && labelValue) labelValue.textContent = label;
            });
        }

        normalizeOptionName(name = '') {
            return name.trim().toLowerCase().replaceAll(' ', '-');
        }
    }

    if (!customElements.get('bundle-picker')) {
        customElements.define('bundle-picker', BundlePicker);
    }

    const pickers = document.querySelectorAll('.bundle-picker');

    pickers.forEach((picker) => {
        if (!picker) return;

        const updateLegendText = () => {
            picker.changeLagendText?.call(picker);
        };

        const updateBundleSelectionInput = (event) => {
            updateLegendText();

            const root = picker.closest('.product') || picker.closest('.card') || picker.parentElement;
            if (!root) return;

            const bundleInput = root.querySelector('.bundle-app-selector input[name="properties[_bundle_selection]"]');
            if (!bundleInput) return;

            const selects = root.querySelectorAll('.bundle-app-selector select');
            const values = [...selects].map((select) => select.value).filter(Boolean);

            bundleInput.value = values.join(' <> ');
        };

        picker.addEventListener('bundle-picker:init', updateBundleSelectionInput);
        picker.addEventListener('bundle-picker:change', updateBundleSelectionInput);
    });
});